import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ApiUrl } from '../../../shared/models/apiurl.models';
import {
  BusinessConfig,
  BusinessDomain,
  BusinessService,
  BusinessStatus,
} from '../../../services/business.service';

type LicenseStatus = '' | 'PENDING' | 'VERIFIED' | 'CORRECTION';

interface OwnerStore {
  id: number;
  name: string;
  subtitle: string;
  subtitle_status: 'PENDING' | 'APPROVED' | 'REJECTED';
  description: string;
  logo_url: string | null;
  is_active: boolean;
  phone: string;
  website: string;
  city: string;
  // SCRUM-260 / 261
  domains: string[];
  service_areas: string[];
  contact_person: string;
  business_id: string;
  license_type: string;
  license_number: string;
  license_status: LicenseStatus;
  license_note: string;
  requires_license: boolean;
  verified_text: string;
  // SCRUM-287 — business contact details on ads
  show_contact_in_ads?: boolean;
  contact_fields?: string[];
}

/** SCRUM-287 — the details a business may show in the "פרטי העסק" block. */
export const CONTACT_FIELD_OPTIONS: { key: string; label: string }[] = [
  { key: 'name', label: 'שם העסק ולוגו' },
  { key: 'license', label: 'מספר רישיון' },
  { key: 'contact_person', label: 'איש קשר ותמונה' },
  { key: 'phone', label: 'טלפון העסק' },
  { key: 'website', label: 'אתר העסק (לפי המסלול)' },
];

/**
 * SCRUM-88 — the store / business identity editor.
 *
 * Lives in the personal area beside the profile, as an optional extension of
 * who you are on the site.
 *
 * SCRUM-116 follow-up: having a store is now a VIP-only entitlement. The
 * check here is UX only (it hides the editor and shows an upgrade prompt
 * instead) — the real boundary is server-side: GET/PUT `store/my/` both
 * gate on `is_vip_now(request.user)` regardless of what this component does.
 */
@Component({
  selector: 'app-store',
  templateUrl: './store.component.html',
  styleUrls: ['./store.component.scss'],
  standalone: false,
})
export class StoreComponent implements OnInit {
  /** null while loading, or when the user has no store yet. */
  store: OwnerStore | null = null;

  name = '';
  subtitle = '';
  description = '';
  phone = '';
  website = '';
  city = '';
  // SCRUM-260 business profile
  contactPerson = '';
  businessId = '';
  domains: string[] = [];
  serviceAreas: string[] = [];
  areaDraft = '';
  licenseType = '';
  licenseNumber = '';
  // SCRUM-287
  showContactInAds = false;
  contactFields: string[] = CONTACT_FIELD_OPTIONS.map(o => o.key);
  readonly contactFieldOptions = CONTACT_FIELD_OPTIONS;

  /** SCRUM-259 — labels, domains, regulated domains. */
  config: BusinessConfig | null = null;
  /** SCRUM-265 — ACTIVE / GRACE / LAPSED / NONE. */
  status: BusinessStatus | null = null;
  /** Arrived here right after buying VIP (SCRUM-260 onboarding). */
  onboarding = false;

  logoFile: File | null = null;
  /** Local object URL for a freshly picked file, before it is uploaded. */
  logoPreview: string | null = null;

  loading = true;
  saving = false;
  deleting = false;
  hasStore = false;

  /** true once we know whether the user is currently VIP */
  vipChecked = false;
  isVip = false;

  error = '';
  success = '';
  logoError = '';

  readonly maxLogoBytes = 2 * 1024 * 1024;
  readonly allowedLogoTypes = ['image/png', 'image/jpeg', 'image/webp'];
  readonly nameMaxLength = 120;
  readonly subtitleMaxLength = 80;
  readonly descriptionMaxLength = 2000;

  constructor(
    private http: HttpClient,
    private apiUrl: ApiUrl,
    private business: BusinessService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.onboarding = this.route.snapshot.queryParamMap.get('onboarding') === '1';
    this.business.config().subscribe((c) => (this.config = c));
    this.checkVipThenLoad();
  }

  /**
   * A business profile is VIP-only. Uses the resolved business status
   * (SCRUM-265), NOT the raw `is_vip` flag — that one stays true for a
   * lapsed subscriber. During the grace period the profile is shown
   * read-only with a renewal prompt.
   */
  private checkVipThenLoad(): void {
    this.business.status().subscribe((st) => {
      this.status = st;
      this.isVip = st?.state === 'ACTIVE' || st?.state === 'GRACE';
      this.vipChecked = true;
      if (this.isVip) {
        this.load();
      } else {
        this.loading = false;
      }
    });
  }

  /** Grace period: still a business, but no edits until renewal. */
  get readOnly(): boolean {
    return this.status?.state === 'GRACE';
  }

  get domainOptions(): BusinessDomain[] {
    return this.config?.domains || [];
  }

  get regulatedSelected(): boolean {
    const regulated = this.config?.regulated_domains || ['NADLAN'];
    return this.domains.some((d) => regulated.includes(d));
  }

  get licenseStatus(): LicenseStatus {
    return this.store?.license_status || '';
  }

  isDomainSelected(code: string): boolean {
    return this.domains.includes(code);
  }

  toggleDomain(code: string): void {
    if (this.readOnly) return;
    this.domains = this.isDomainSelected(code)
      ? this.domains.filter((d) => d !== code)
      : [...this.domains, code];
  }

  addArea(): void {
    const v = (this.areaDraft || '').trim();
    if (v && !this.serviceAreas.includes(v) && this.serviceAreas.length < 30) {
      this.serviceAreas = [...this.serviceAreas, v];
    }
    this.areaDraft = '';
  }

  removeArea(a: string): void {
    this.serviceAreas = this.serviceAreas.filter((x) => x !== a);
  }

  skipOnboarding(): void {
    this.router.navigate(['/personal/vip-dashboard']);
  }

  /** The logo to show: a freshly picked file wins over the saved one. */
  get shownLogo(): string | null {
    return this.logoPreview || this.store?.logo_url || null;
  }

  get initials(): string {
    const t = (this.name || '').trim();
    if (!t) return '?';
    const parts = t.split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  /**
   * The subtitle is a public claim shown next to a VIP badge — the epic's own
   * example is "סוחר רכב מורשה", a claim of licensure. The backend can be
   * switched to review-before-publish, so the editor has to be able to say
   * "pending" rather than silently showing text nobody else can see.
   */
  get subtitlePending(): boolean {
    return this.store?.subtitle_status === 'PENDING';
  }

  get subtitleRejected(): boolean {
    return this.store?.subtitle_status === 'REJECTED';
  }

  get canSave(): boolean {
    return !this.saving && !this.readOnly && this.name.trim().length > 0 && !this.logoError
      && (!this.regulatedSelected || this.licenseNumber.trim().length > 0);
  }

  private get authOptions() {
    return {
      headers: new HttpHeaders({ Authorization: this.apiUrl.getJWTToken() }),
    };
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.http
      .get<OwnerStore>(this.apiUrl.getStoreUrl(), {
        headers: new HttpHeaders({ Authorization: this.apiUrl.getJWTToken() }),
        observe: 'response',
      })
      .subscribe({
        next: (res) => {
          this.loading = false;
          // 204 is the normal "you have no store" answer, not an error.
          if (res.status === 204 || !res.body) {
            this.hasStore = false;
            return;
          }
          this.applyStore(res.body);
        },
        error: () => {
          this.loading = false;
          this.error = 'לא הצלחנו לטעון את פרטי החנות';
        },
      });
  }

  private applyStore(store: OwnerStore): void {
    this.store = store;
    this.hasStore = true;
    this.name = store.name || '';
    this.subtitle = store.subtitle || '';
    this.description = store.description || '';
    this.phone = store.phone || '';
    this.website = store.website || '';
    this.city = store.city || '';
    this.contactPerson = store.contact_person || '';
    this.businessId = store.business_id || '';
    this.domains = [...(store.domains || [])];
    this.serviceAreas = [...(store.service_areas || [])];
    this.licenseType = store.license_type || '';
    this.licenseNumber = store.license_number || '';
    this.showContactInAds = store.show_contact_in_ads === true;
    this.contactFields = Array.isArray(store.contact_fields)
      ? [...store.contact_fields] : CONTACT_FIELD_OPTIONS.map(o => o.key);
    this.logoFile = null;
    this.revokePreview();
  }

  onLogoPicked(event: Event): void {
    this.logoError = '';
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    if (!file) return;

    if (!this.allowedLogoTypes.includes(file.type)) {
      this.logoError = 'פורמט נתמך: PNG, JPEG או WEBP בלבד';
      input.value = '';
      return;
    }
    if (file.size > this.maxLogoBytes) {
      this.logoError = 'הלוגו חייב להיות עד 2MB';
      input.value = '';
      return;
    }

    this.revokePreview();
    this.logoFile = file;
    this.logoPreview = URL.createObjectURL(file);
  }

  clearPickedLogo(): void {
    this.logoFile = null;
    this.revokePreview();
    this.logoError = '';
  }

  private revokePreview(): void {
    if (this.logoPreview) {
      URL.revokeObjectURL(this.logoPreview);
      this.logoPreview = null;
    }
  }

  save(): void {
    if (!this.canSave) return;
    this.saving = true;
    this.error = '';
    this.success = '';

    // multipart, not JSON: the logo is a real file upload. Content-Type is
    // deliberately NOT set — the browser must add its own multipart boundary.
    const form = new FormData();
    form.append('name', this.name.trim());
    form.append('subtitle', this.subtitle.trim());
    form.append('description', this.description.trim());
    form.append('phone', this.phone.trim());
    form.append('website', this.normalizedWebsite);
    form.append('city', this.city.trim());
    form.append('contact_person', this.contactPerson.trim());
    form.append('business_id', this.businessId.trim());
    // Lists go as JSON strings — the server accepts that from multipart.
    form.append('domains', JSON.stringify(this.domains));
    form.append('service_areas', JSON.stringify(this.serviceAreas));
    form.append('license_type', this.licenseType);
    form.append('license_number', this.licenseNumber.trim());
    form.append('show_contact_in_ads', this.showContactInAds ? 'true' : 'false');
    form.append('contact_fields', JSON.stringify(this.contactFields));
    if (this.logoFile) {
      form.append('logo', this.logoFile, this.logoFile.name);
    }

    this.http
      .put<OwnerStore>(this.apiUrl.getStoreUrl(), form, this.authOptions)
      .subscribe({
        next: (store) => {
          this.saving = false;
          this.applyStore(store);
          this.success = 'פרטי העסק נשמרו';
          setTimeout(() => (this.success = ''), 4000);
          if (this.onboarding) {
            this.onboarding = false;
            this.router.navigate([], { queryParams: {} });
          }
        },
        error: (err) => {
          this.saving = false;
          this.error = this.readError(err);
        },
      });
  }

  /** Surfaces the server's field errors instead of a generic failure. */
  private readError(err: any): string {
    const body = err?.error;
    if (typeof body === 'string') return body;
    if (body && typeof body === 'object') {
      for (const key of ['name', 'license_number', 'domains', 'business_id', 'website', 'contact_fields',
                         'logo', 'subtitle', 'description', 'detail']) {
        const v = body[key];
        if (Array.isArray(v) && v.length) return String(v[0]);
        if (typeof v === 'string') return v;
      }
    }
    return 'לא הצלחנו לשמור את פרטי החנות';
  }

  /** "example.co.il" → "https://example.co.il" (the server wants a full URL). */
  private get normalizedWebsite(): string {
    const w = this.website.trim();
    if (!w) return '';
    return /^https?:\/\//i.test(w) ? w : 'https://' + w;
  }

  isContactFieldOn(key: string): boolean {
    return this.contactFields.includes(key);
  }

  toggleContactField(key: string): void {
    this.contactFields = this.isContactFieldOn(key)
      ? this.contactFields.filter(k => k !== key)
      : [...this.contactFields, key];
  }

  remove(): void {
    if (this.deleting) return;
    // Deleting removes the public store page and the branding on every one of
    // this user's ads, so it is worth one confirmation.
    if (!confirm('למחוק את החנות? המיתוג ייעלם מכל המודעות שלך.')) return;

    this.deleting = true;
    this.http.delete(this.apiUrl.getStoreUrl(), this.authOptions).subscribe({
      next: () => {
        this.deleting = false;
        this.store = null;
        this.hasStore = false;
        this.name = '';
        this.subtitle = '';
        this.description = '';
        this.clearPickedLogo();
        this.success = 'החנות נמחקה';
        setTimeout(() => (this.success = ''), 4000);
      },
      error: () => {
        this.deleting = false;
        this.error = 'לא הצלחנו למחוק את החנות';
      },
    });
  }
}
